import fp from 'lodash/fp';
import { goBack, push } from 'react-router-redux';
import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { TaskStatus } from '../../../../constants/tasks';
import { apiActionResult } from '../../common';
import { authTokenSelector } from '../../session';
import { apiActions as userApiActions, default as userActions } from '../../user/actions';
import { actions, apiActions } from './actions';

export function* handleBusinessResource(action) {
  const authToken = yield select(authTokenSelector);
  if (authToken.idToken) {
    switch (action.type) {
      case actions.business.user.getIndustryRoles().type: {
        const { businessId, userId } = action.payload;
        yield put(apiActions.business.user.industryRoles(authToken, businessId, userId));
        break;
      }
      case actions.business.user.getCpdActivities().type: {
        const { businessId, userId, query } = action.payload;
        yield put(apiActions.business.user.getCpdActivities(authToken, businessId, userId, query));
        break;
      }
      case actions.business.user.loadMore().type: {
        const { businessId, userId, query } = action.payload;
        yield put(apiActions.business.user.loadMore(authToken, businessId, userId, query));
        break;
      }
      case actions.business.user.getCpdCategorys().type: {
        const { businessId, userId, query } = action.payload;
        yield put(apiActions.business.user.getCpdCategorys(authToken, businessId, userId, query));
        break;
      }
      case actions.business.user.getQualifications().type: {
        const { businessId, userId } = action.payload;
        yield put(apiActions.business.user.getQualifications(authToken, businessId, userId));
        break;
      }
      case actions.business.user.getProfile().type: {
        const { businessId, userId } = action.payload;
        yield put(apiActions.business.user.getProfile(authToken, businessId, userId));
        break;
      }
      case actions.business.user.getSnapshotReport().type: {
        const { businessId, userId, snapshotId } = action.payload;
        yield put(apiActions.business.user.getSnapshotReport(authToken, businessId, userId, snapshotId));
        break;
      }
      case actions.business.user.getExternalCpd().type: {
        const {
          businessId,
          userId,
          externalCpdId
        } = action.payload;

        yield put(apiActions.business.user.getExternalCpd(authToken, businessId, userId, externalCpdId));

        break;
      }
      case actions.business.user.updateExternalCpd().type: {
        const {
          userId,
          externalCpd,
          cpdCategoryPoints,
          goals,
          files,
          businessId,
          nextRoute
        } = action.payload;

        const contentFormData = {
          data: {
            externalCpd: {
              ...externalCpd,
              files: undefined,
              existingFiles: undefined
            },
            goals,
            cpdCategoryPoints: cpdCategoryPoints,
            files: externalCpd.existingFiles ? externalCpd.existingFiles : []
          },
          file: files
        };

        /* Add an External CPD for the user */
        yield put(apiActions.business.user.updateExternalCpd(authToken, businessId, userId, externalCpd.id, contentFormData));

        /* Wait for the result */
        const externalCpdResult = yield apiActionResult(actions.business.user.api.updateExternalCpd);

        if (externalCpdResult.success) {
          if (nextRoute) {
            yield put(push({ pathname: nextRoute }));
          } else {
            yield put(goBack());
          }
        }

        break;
      }
      case actions.business.user.addExternalCpd().type: {
        const {
          userIds,
          externalCpd,
          cpdCategoryPoints,
          goals,
          files,
          businessId,
          nextRoute,
          teamManagerId,
          teamManagerStatus,
        } = action.payload;

        /**
         * Takes a set of user IDs and recursively adds their External CPD hours one by one as long
         * as each is successful.  Once all have been successful, redirects the user to the education
         * page. 
         * @param {*} remainingUserIds 
         */
        function* recursiveAddExternalCpd(remainingUserIds) {
          const authToken = yield select(authTokenSelector);

          const uploadUserId = fp.head(remainingUserIds);

          /* The external CPD status might be a different one if upload user is a team manager */
          const externalCpdStatus = uploadUserId === teamManagerId 
            /* If team manager status is not defined, use default status instead */
            ? teamManagerStatus || externalCpd.status
            : externalCpd.status;

          const contentFormData = {
            data: {
              externalCpd: {
                ...externalCpd,
                files: undefined,
                existingFiles: undefined,
                status: externalCpdStatus,
              },
              cpdCategoryPoints: cpdCategoryPoints,
              goals,
              files: externalCpd.existingFiles ? externalCpd.existingFiles : []
            },
            file: files
          };

          /* Add an External CPD for the user */
          yield put(apiActions.business.user.addExternalCpd(authToken, businessId, uploadUserId, contentFormData));

          /* Wait for the result */
          const externalCpdResult = yield apiActionResult(actions.business.user.api.addExternalCpd);

          /* Recursively send External CPD hours or redirect if done */
          if (externalCpdResult.success) {
            if (remainingUserIds.length <= 1) {
              yield put(push({ pathname: nextRoute }));
            } else {
              yield recursiveAddExternalCpd(fp.tail(remainingUserIds))
            }
          }
        }
        yield recursiveAddExternalCpd(userIds)

        /* Update user total progress */
        yield put(userApiActions.user.getCpdCategory(authToken));

        break;
      }
      case actions.business.user.deleteExternalCpd().type: {
        const {
          businessId,
          userId,
          externalCpdId,
          nextRoute
        } = action.payload;

        yield put(apiActions.business.user.deleteExternalCpd(authToken, businessId, userId, externalCpdId))

        const externalCpdResult = yield apiActionResult(actions.business.user.api.deleteExternalCpd);

        if (externalCpdResult.success) {
          if (nextRoute) {
            yield put(push({ pathname: nextRoute }));
          } else {
            yield put(goBack());
          }
        }

        break;
      }
      default:
        console.error(`Unknown authenticated action received [${action.type}].`);
    }
  }
  else {
    switch (action.type) {
      default:
        console.error(`Unknown common action received [${action.type}].`);
    }
  }
}

export function* setCpdCategorys(action) {
  const authToken = yield select(authTokenSelector);

  if (authToken.idToken) {
    const {
      cpdCategorys,
      businessId,
      industryId,
      industry,
      userId,
      currentUserId,
      goals
    } = action.payload;

    yield put(actions.business.user.updateTaskStack(action.payload.userId, TaskStatus.updating));

    yield put(apiActions.business.user.updateIndustryRole(authToken, businessId, userId, industry));
    const updateIndustryRoleResult = yield apiActionResult(actions.business.user.api.updateIndustryRole);

    if (updateIndustryRoleResult.success) {
      yield put(apiActions.business.user.setProgressGoals(authToken, businessId, userId, goals));
      const updateProgressGoalsResult = yield apiActionResult(actions.business.user.api.setProgressGoals);

      if (updateProgressGoalsResult.success) {
        yield put(apiActions.business.user.setCpdCategorys(authToken, businessId, userId, {
          industryId: industryId,
          pendingCpdCategorys: cpdCategorys
        }));
        const updateCpdCategoryResult = yield apiActionResult(actions.business.user.api.setCpdCategorys)

        if (updateCpdCategoryResult.success) {
          if (currentUserId === userId) {
            yield put(userActions.user.getCpdCategorys(currentUserId));
          }
          yield put(actions.business.user.updateTaskStack(action.payload.userId, TaskStatus.completed, {
            ...updateIndustryRoleResult.success.payload,
            trainingPlan: fp.map(
              ({ cpdCategoryDetail }) => cpdCategoryDetail
            )(updateCpdCategoryResult.success.payload.cpdCategorys.cpdCategorys)
          }))
        } else { yield put(actions.business.user.updateTaskStack(action.payload.userId, TaskStatus.completed)) }
      } else { yield put(actions.business.user.updateTaskStack(action.payload.userId, TaskStatus.completed)) }
    } else { yield put(actions.business.user.updateTaskStack(action.payload.userId, TaskStatus.completed)) }
  }
}

export const sagas = all([
  takeLatest([
    actions.business.user.getIndustryRoles().type,
    actions.business.user.getCpdActivities().type,
    actions.business.user.loadMore().type,
    actions.business.user.getCpdCategorys().type,
    actions.business.user.addExternalCpd().type,
    actions.business.user.getExternalCpd().type,
    actions.business.user.updateExternalCpd().type,
    actions.business.user.deleteExternalCpd().type,
    actions.business.user.getQualifications().type,
    actions.business.user.getProfile().type,
    actions.business.user.getSnapshotReport().type
  ], handleBusinessResource),
  takeEvery(actions.business.user.setCpdCategorys().type, setCpdCategorys)
])

function* userSagas() {
  yield sagas;
}

export default userSagas;


// WEBPACK FOOTER //
// ./src/state/modules/business/user/sagas.js